"use client";

import { useState } from "react";
import { useRouter } from "next/navigation";
import { ArrowRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import {
  CONTACT_SUBJECTS,
  contactFormSchema,
  submitContactForm,
  sanitizePhoneInput,
  zodFieldErrors,
  type ContactFormRequest,
} from "@/lib/api/forms";

const initialForm: ContactFormRequest = {
  fullName: "",
  email: "",
  phone: "",
  subject: "Request Quote",
  message: "",
};

export function ContactInquiryForm() {
  const router = useRouter();
  const [formData, setFormData] = useState<ContactFormRequest>(initialForm);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);

  const update = (patch: Partial<ContactFormRequest>) => {
    setFormData((prev) => ({ ...prev, ...patch }));
    const keys = Object.keys(patch);
    setFieldErrors((prev) => {
      const next = { ...prev };
      keys.forEach((k) => delete next[k]);
      return next;
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const parsed = contactFormSchema.safeParse(formData);
    if (!parsed.success) {
      const errors = zodFieldErrors(parsed.error);
      setFieldErrors(errors);
      toast.error(
        Object.values(errors)[0] ?? "Please fix the highlighted fields",
      );
      return;
    }

    setSubmitting(true);
    setFieldErrors({});
    const result = await submitContactForm(parsed.data);
    setSubmitting(false);

    if (result.success) {
      toast.success(result.message || "Message sent successfully");
      setFormData(initialForm);
      router.push("/thankYou");
      return;
    }

    if (result.errors?.length) {
      const fromApi: Record<string, string> = {};
      result.errors.forEach(({ field, message }) => {
        if (!fromApi[field]) fromApi[field] = message;
      });
      setFieldErrors(fromApi);
      toast.error(result.errors[0].message);
      return;
    }

    toast.error(result.message || "Something went wrong. Please try again.");
  };

  const inputClass = (name: string) =>
    `w-full bg-background border rounded-md px-4 py-3 text-sm focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition ${
      fieldErrors[name] ? "border-destructive" : "border-border"
    }`;

  return (
    <form onSubmit={handleSubmit} className="bg-card border border-border rounded-md p-5 sm:p-8 space-y-5">
      <div>
        <h2 className="font-display text-base text-foreground">Send a Message</h2>
        <p className="text-muted-foreground text-sm mt-1">Fill in your details below.</p>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label className="text-xs font-semibold text-foreground tracking-wide uppercase block mb-1.5">
            Full Name
          </label>
          <input
            value={formData.fullName}
            onChange={(e) => update({ fullName: e.target.value })}
            className={inputClass("fullName")}
            placeholder="First and last name"
            autoComplete="name"
          />
          {fieldErrors.fullName && (
            <p className="text-destructive text-xs mt-1">{fieldErrors.fullName}</p>
          )}
        </div>
        <div>
          <label className="text-xs font-semibold text-foreground tracking-wide uppercase block mb-1.5">
            Email
          </label>
          <input
            type="email"
            value={formData.email}
            onChange={(e) => update({ email: e.target.value })}
            className={inputClass("email")}
            placeholder="your@email.com"
            autoComplete="email"
          />
          {fieldErrors.email && (
            <p className="text-destructive text-xs mt-1">{fieldErrors.email}</p>
          )}
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label className="text-xs font-semibold text-foreground tracking-wide uppercase block mb-1.5">
            Phone
          </label>
          <input
            type="tel"
            value={formData.phone}
            onChange={(e) => update({ phone: sanitizePhoneInput(e.target.value) })}
            className={inputClass("phone")}
            placeholder="Digits only, 7–15"
            autoComplete="tel"
          />
          {fieldErrors.phone && (
            <p className="text-destructive text-xs mt-1">{fieldErrors.phone}</p>
          )}
        </div>
        <div>
          <label className="text-xs font-semibold text-foreground tracking-wide uppercase block mb-1.5">
            Subject
          </label>
          <select
            value={formData.subject}
            onChange={(e) =>
              update({ subject: e.target.value as ContactFormRequest["subject"] })
            }
            className={inputClass("subject")}
          >
            {CONTACT_SUBJECTS.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
          {fieldErrors.subject && (
            <p className="text-destructive text-xs mt-1">{fieldErrors.subject}</p>
          )}
        </div>
      </div>

      <div>
        <label className="text-xs font-semibold text-foreground tracking-wide uppercase block mb-1.5">
          Message
        </label>
        <textarea
          rows={6}
          value={formData.message}
          onChange={(e) => update({ message: e.target.value })}
          className={`${inputClass("message")} resize-none`}
          placeholder="Tell us about your project or question..."
        />
        {fieldErrors.message && (
          <p className="text-destructive text-xs mt-1">{fieldErrors.message}</p>
        )}
      </div>

      <button
        type="submit"
        disabled={submitting}
        className="w-full inline-flex items-center justify-center gap-2 bg-primary text-primary-foreground py-3.5 rounded-md font-bold text-sm tracking-wide hover:bg-primary/90 transition-all disabled:opacity-60"
      >
        {submitting ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin" /> Sending...
          </>
        ) : (
          <>
            Send Message <ArrowRight className="w-4 h-4" />
          </>
        )}
      </button>
    </form>
  );
}
